import { useI18n } from '@ecom/i18n';
import { EmptyState, Field, Tabs } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useLockedAccountsQuery, useProvisionalPasswordsQuery } from '../api';
import { AccountLookup } from '../components/AccountLookup';
import { LockedAccountsTable } from '../components/LockedAccountsTable';
import { ProvisionalPasswordsTable } from '../components/ProvisionalPasswordsTable';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

type TabId = 'locked' | 'provisional' | 'lookup';

/**
 * Access and passwords (lot B2), for a superadmin: the accounts locked now, the provisional passwords still pending, and the search of
 * one account with its sessions. Like every audited read of this app it needs a stated reason (10 characters at least) typed by the
 * person looking; nothing here decides what is allowed - the API and the database functions do, on every request.
 */
export function AccessPage(): ReactElement {
  const { t } = useI18n();
  const [tab, setTab] = useState<TabId>('locked');
  const [reason, setReason] = useState('');
  const reasonValid = reason.trim().length >= 10;

  const locked = useLockedAccountsQuery(tab === 'locked' ? reason : '');
  const provisional = useProvisionalPasswordsQuery(tab === 'provisional' ? reason : '');

  const reasonRequired = (
    <EmptyState
      title={t('admin.common.reasonRequiredTitle')}
      description={t('admin.accounts.reasonRequiredDescription')}
    />
  );

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.access.title')}</h1>
        <p className="text-sm text-inkFaint">{t('admin.access.subtitle')}</p>
      </div>

      <Tabs
        label={t('admin.access.tab.label')}
        tabs={[
          { id: 'locked', label: t('admin.access.tab.locked') },
          { id: 'provisional', label: t('admin.access.tab.provisional') },
          { id: 'lookup', label: t('admin.access.tab.lookup') },
        ]}
        activeId={tab}
        onChange={(id) => {
          setTab(id as TabId);
        }}
      >
        <div className="flex flex-col gap-5">
          <div className="rounded-xl border border-line bg-surface p-4">
            <Field label={t('admin.common.reasonConsultationLabel')}>
              {(bind) => (
                <input
                  {...bind}
                  type="text"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                  maxLength={500}
                  className="ecom-input"
                  placeholder={t('admin.accounts.reasonPlaceholder')}
                />
              )}
            </Field>
          </div>

          {tab === 'locked' ? (
            !reasonValid ? (
              reasonRequired
            ) : locked.isPending ? (
              <TableSkeleton columns={5} />
            ) : locked.isError ? (
              <EmptyState title={t('admin.access.loadError')} />
            ) : locked.data.accounts.length === 0 ? (
              <EmptyState title={t('admin.access.locked.empty')} />
            ) : (
              <LockedAccountsTable accounts={locked.data.accounts} />
            )
          ) : null}

          {tab === 'provisional' ? (
            !reasonValid ? (
              reasonRequired
            ) : provisional.isPending ? (
              <TableSkeleton columns={4} />
            ) : provisional.isError ? (
              <EmptyState title={t('admin.access.loadError')} />
            ) : provisional.data.accounts.length === 0 ? (
              <EmptyState title={t('admin.access.provisional.empty')} />
            ) : (
              <ProvisionalPasswordsTable accounts={provisional.data.accounts} />
            )
          ) : null}

          {tab === 'lookup' ? <AccountLookup reason={reason} /> : null}
        </div>
      </Tabs>
    </div>
  );
}
